import {defineSlotRecipe} from '@chakra-ui/react3'

/**
 * Slot recipe for the Xleap navigation flyout.
 *
 * The flyout is an in-header expanding disclosure — not an overlay-manager
 * surface (see `navigation-provider.jsx` for the approved exception). One
 * responsive component serves both viewports: a full-screen push-pane on mobile
 * and a megamenu on desktop, switched purely through responsive recipe values
 * so a resize never remounts the tree and open-state survives the breakpoint.
 *
 * - `backdrop` dims the page and closes the nav on click. It is rendered as a
 *   sibling of the header card, outside the clip, and only while open.
 * - `reveal` is the flow sibling of the bar inside the header card, which clips
 *   its block axis. It animates purely in CSS via `gridTemplateRows: 0fr → 1fr`
 *   keyed on `data-state`; no JavaScript measures the panel height.
 * - `panel` is the grid child; `minHeight: 0` lets it collapse to nothing. It
 *   clips its block axis and carries the dialog semantics, but deliberately does
 *   not scroll — see `body`.
 * - `body` wraps the flyout content (the navigation tree + tools nav rendered by
 *   `flyout-navigation.jsx`) and is the internal scroll container, capped to the
 *   viewport minus the reserved header height so the flyout never grows the page
 *   past the viewport.
 */

export const flyoutRecipe = defineSlotRecipe({
    slots: ['backdrop', 'reveal', 'panel', 'body'],
    base: {
        backdrop: {
            position: 'fixed',
            // Reaches the gutter the scroll lock reserves, which the scrollport
            // bleeds into as well (see `body`, STOK-17181).
            inset: 'spacing-0',
            bg: 'background.overlay.subtle',
            border: 'none',
            cursor: 'pointer',
            // The backdrop stays mounted so it can fade *out*; unmounting it on
            // close deletes the animation along with the element. It is the
            // mobile open in its entirety ("fades in the flyout background") and
            // leads the desktop canvas.
            opacity: 0,
            // `visibility` keeps the hidden backdrop out of the a11y tree and off
            // the hit-test, and being animatable it holds the element visible
            // for the whole fade-out instead of cutting it at frame 0.
            visibility: 'hidden',
            // Deliberately *not* in the transition list. A transitioning
            // `visibility` stays `visible` for the entire duration, so a backdrop
            // that relied on it alone would keep swallowing clicks across the
            // whole page for the length of the close.
            pointerEvents: 'none',
            transitionProperty: 'opacity, visibility',
            transitionDuration: {base: 'slower', s: 'slowest'},
            transitionTimingFunction: 'ease-out-strong',
            _motionReduce: {
                transitionDuration: '0.01ms'
            },
            _open: {
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                // Applied only while open, and never transitioned. Re-blurring
                // the whole viewport every frame is the most expensive thing
                // the flyout could ask for, and a standing backdrop-filter
                // inside a sticky header is a known iOS compositing hazard — so
                // it neither animates nor persists. The opacity fade above still
                // carries it in, because opacity composites the element's
                // rendered result, backdrop-filter included.
                backdropFilter: 'blur(12px)'
            },
            // Negative layer within the sticky header's stacking context: it
            // still dims all page content beneath the
            // header, but paints below the opaque header card (`zIndex: docked`)
            // so the header reads as a solid surface over the backdrop instead
            // of being dimmed.
            zIndex: 'hide'
        },
        reveal: {
            display: 'grid',
            transitionProperty: 'grid-template-rows',
            // Only desktop expands a canvas. Figma's motion cards give the
            // desktop "Canvas Expand" (node 34407-35541) 1s on
            // cubic-bezier(0.5, 0, 0, 1) and give mobile no canvas animation at
            // all — mobile has only "Slide in" (node 34407-35356), which is the
            // content cascade, not the panel. So the row change is instant
            // there and the panel fades instead; see `panel` below for why it
            // could never have animated open on mobile anyway.
            // The 1s has no token: DESIGN-ISSUE(STOK-17031), the durations scale
            // tops out at `slowest` (500ms), so it stays a literal.
            transitionDuration: {base: '0s', s: '1s'},
            transitionTimingFunction: {
                base: 'ease-out-strong',
                s: 'ease-in-out-strong'
            },
            // Closing, the instant row change has to wait out the panel's fade —
            // otherwise the flyout is clipped away in the first frame and there
            // is nothing left to see fading. Opening needs no such wait, so the
            // open state clears it.
            //
            // Named as a token reference rather than by bare token name:
            // `transitionDelay` is not on the `durations` scale in Chakra the way
            // `transitionDuration` is, so a bare `slower` resolves to nothing and
            // computes as `0s` — which silently drops the wait this depends on.
            transitionDelay: {base: '{durations.slower}', s: '0s'},
            _motionReduce: {
                transitionDuration: '0.01ms',
                transitionDelay: '0ms'
            },
            _closed: {
                gridTemplateRows: '0fr'
            },
            _open: {
                gridTemplateRows: '1fr',
                transitionDelay: '0s'
            }
        },
        panel: {
            // The cap the flyout is sized against: the open card (bar + panel)
            // never grows past the viewport and, from the `s` breakpoint up,
            // leaves the same block offset below the card that the card is inset
            // by above it. Declared here and applied on `body`, the scrollport —
            // content taller than this scrolls there. Two subtractions from
            // `100dvh`:
            //   - `--header-viewport-offset-block`: how far the header's top
            //     edge sits below the top of the viewport — the message bar
            //     above it. `header/index.recipe.js` states the resting value,
            //     and `header/index.jsx` writes the measured distance over it
            //     while the flyout is open. That matters because the message bar
            //     scrolls away: the resting value keeps room for a bar that has
            //     left, which shortens the panel by its height (64px mobile,
            //     44px from `s`) and leaves a strip of backdrop at its foot
            //     (STOK-17548).
            //   - `--header-bar-block-size`: the header card's own footprint —
            //     the bar, its card borders and the block inset above and below
            //     it. The box as painted, not the clearance, which collapses
            //     this term for the floating variant even though the card is on
            //     screen there too.
            '--flyout-available-block-size':
                'calc(100dvh - var(--header-viewport-offset-block, 0px) - var(--header-bar-block-size))',
            minHeight: 'spacing-0',
            // Clips the block axis, so the `reveal` grid can collapse to `0fr`
            // over the full-height `body` without that axis ever scrolling. The
            // panel must not be the scrollport: it is the box the reveal
            // animates, so its client height sweeps 0 → full while the content
            // height stays put, leaving `overflow-y: auto` overflowing — and so
            // painting a scrollbar — for the whole transition, in both
            // directions, even when the settled flyout fits (STOK-17456).
            //
            // The inline axis stays visible so the scrollport below can bleed
            // past the card (see `body`). Pair `visible` with `clip`, never
            // `hidden` — that computes back to `auto` and makes this a
            // scrollport again.
            overflowX: 'visible',
            overflowY: 'clip',
            // The closed reveal must remain content-sized so its grid row can collapse.
            blockSize: 'auto',
            // Mobile fades the flyout in and out; desktop expands the canvas.
            // Figma asks for exactly this — mobile has no canvas-expand card,
            // only the content "Slide in" — and it makes the close the reverse
            // of the open rather than a different animation. It is also the only
            // thing that *can* work at that breakpoint: the reveal's row is
            // floored by whatever definite height the mobile panel is given, so
            // the row transition had nothing to interpolate on the way in. See
            // `minBlockSize` on `body` for the measurement.
            opacity: {base: 0, s: 1},
            transitionProperty: 'opacity',
            transitionDuration: {base: 'slower', s: '0s'},
            transitionTimingFunction: 'ease-out-strong',
            _motionReduce: {
                transitionDuration: '0.01ms'
            },
            "[data-state='open'] &": {
                opacity: 1
            }
        },
        body: {
            // The block inset lives on the tree root inside, not here — see
            // `--flyout-scroll-inset` in `flyout-navigation.recipe.js`. It
            // varies by level, and `handleResize` measures that box, so an
            // inset above it would step the panel instead of travelling it.
            //
            // Every open mobile navigation level fills the available viewport
            // below the bar. Stated here rather than as a `block-size` on the
            // panel, and unconditionally rather than only while open, for one
            // reason: a definite size on the panel floors the reveal's flexible
            // grid track at that size — measured, a `0fr` row resolving to
            // 1612px instead of 0. That clamps the row to full height from frame
            // one, making the mobile open instant, and once the open state
            // releases it again the close starts with the flyout jumping down to
            // its content height before it has faded.
            //
            // On the body the same fill costs nothing: the panel clips its
            // block axis at `min-height: 0`, so its own minimum contribution to
            // the track stays 0 and the row still collapses — while the rendered
            // height holds steady for the whole fade-out.
            minBlockSize: {base: 'var(--flyout-available-block-size)', s: 'auto'},
            // The scrollport. The body is not the box the reveal animates, so
            // its client height is `min(content, cap)` for the whole open and
            // close — a settled flyout that fits therefore stays scrollbar-free
            // across both, rather than overflowing for the length of every
            // transition (STOK-17456). Content-driven motion inside the
            // scrollport is a separate matter: the `nav-resize` height keyframe
            // and the 1rem the exit animation ends on can each cross the cap for
            // the length of their own animation.
            maxBlockSize: 'var(--flyout-available-block-size)',
            // Both axes stated. `overflow-y` alone would leave `overflow-x`
            // computing to `auto` rather than `visible` (CSS Overflow 3 §3.3,
            // the trap `panes` also documents), which makes the flyout
            // pannable sideways. The level-1 teaser column is a fixed 327px and
            // sits outside the `overflowX: clip` that guards `panes`, so the
            // scrollable width settles at `24px + 327px`: every viewport under
            // 351px can be dragged off-centre, measured as 31px of travel at
            // 320px. Whether that also paints an inline scrollbar is a platform
            // question — it does not on overlay-scrollbar systems — but the
            // panning happens everywhere.
            overflowX: 'hidden',
            overflowY: 'auto',
            // The flyout's own surface on mobile, where the fade carries it in;
            // on the card it would appear the instant the row opened, ahead of
            // the fade. On the scrollport rather than `panel` because this is
            // the box that bleeds — a level up it stops at the card's edge and
            // leaves the bled strip dimmed.
            bg: {base: 'primary.base.white', s: 'transparent'},
            // Bleeds the scrollport's inline-end edge out to the viewport so
            // its scrollbar lands where the page's own was, rather than inset
            // inside the header card (STOK-17181): negative margin out, equal
            // padding back, content box unmoved. The `min` zeroes the distance
            // wherever no scrollbar holds layout space, which also ends the
            // bleed at the start of the close rather than its end. Derived in
            // `docs/agents/navigation-tree.md`.
            '--flyout-scroll-bleed-inline-end':
                'min(calc(50vw - 50% + var(--scrollbar-width, 0px) / 2), calc(var(--scrollbar-width, 0px) * 1000))',
            marginInlineEnd: 'calc(-1 * var(--flyout-scroll-bleed-inline-end))',
            // The panes start on the logo's line, so from `s` up this is the
            // bar's own content edge, published once on the header root. Mobile
            // keeps the page gutter less the card inset.
            paddingInlineStart: {
                base: 'calc(var(--container-inline-padding) - var(--app-header-offset-inline))',
                s: 'var(--desktop-inline-padding)'
            },
            paddingInlineEnd: {
                base: 'calc(var(--container-inline-padding) - var(--app-header-offset-inline) + var(--flyout-scroll-bleed-inline-end))',
                s: 'calc(var(--desktop-inline-padding) + var(--flyout-scroll-bleed-inline-end))'
            }
        }
    }
})
